import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@mui/material';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import React from 'react';

import { Copy } from 'src/common/ui/components';
import useTypedSWR from 'src/components/general/useTypedSWR';
import { GET_PATIENT_PROFILE_PHOTO } from 'src/idVerification/queries/getPatientProfilePhoto.gql';

const PHOTO_SIZE = 120;

type PatientPhotoDisplayProps = {
  patientId: string;
};

export const PatientPhotoDisplay: React.FC<PatientPhotoDisplayProps> = ({ patientId }) => {
  const styles = useStyles();
  const { data, isLoading, error } = useTypedSWR([GET_PATIENT_PROFILE_PHOTO, { id: patientId }], {
    revalidateOnFocus: false,
  });

  if (error) {
    return <Copy variant="body2">Error loading profile photo</Copy>;
  }

  if (isLoading) {
    return <Skeleton variant="rectangular" height={PHOTO_SIZE} width={PHOTO_SIZE} />;
  }

  if (!data?.menuPatient?.profilePhoto) {
    return <Copy variant="body2">No profile photo available</Copy>;
  }

  const photoUrl = data.menuPatient.profilePhoto;
  const patientName = `${data.menuPatient.firstName} ${data.menuPatient.lastName}`;
  const patientDobFormatted = data.menuPatient.dob
    ? format(parse(data.menuPatient.dob, 'yyyy-MM-dd', new Date()), 'MM-dd-yyyy')
    : 'unknown';

  return (
    <div className={styles.container}>
      <div className={styles.photoContainer}>
        <div
          className={styles.photo}
          style={{ backgroundImage: `url(${photoUrl})` }}
          title={patientName}
        />
      </div>
      <Copy variant="body1">{patientName}</Copy>
      <Copy variant="body2">DOB: {patientDobFormatted}</Copy>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  photoContainer: {
    overflow: 'hidden',
    borderRadius: '50%',
  },
  photo: {
    width: PHOTO_SIZE,
    height: PHOTO_SIZE,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
});
